import React, { useState } from 'react';
import { Mail, Lock, ArrowRight, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // 👈 1. Импорт
import api from '../api/api';

interface AuthProps {
  onLogin: () => void;
  isRegister?: boolean; 
}

export const Auth: React.FC<AuthProps> = ({ onLogin, isRegister = false }) => {
  const { t } = useTranslation(); // 👈 2. Хук
  
  const [isLoginMode, setIsLoginMode] = useState(!isRegister);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isLoginMode ? '/auth/login' : '/auth/register';
      const res = await api.post(endpoint, { email, password });
      
      if (!isLoginMode) {
          // После успешной регистрации сразу делаем логин
          const loginRes = await api.post('/auth/login', { email, password });
          if (loginRes.data.access_token) {
              localStorage.setItem('token', loginRes.data.access_token);
              onLogin();
          }
      } else {
          if (res.data.access_token) {
              localStorage.setItem('token', res.data.access_token);
              onLogin(); 
          }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f172a] p-4 relative overflow-hidden">
      {/* Фоновые эффекты */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/10 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/10 rounded-full blur-[100px]"></div>
      </div>

      <div className="w-full max-w-md bg-[#1e293b] border border-slate-800 rounded-3xl p-8 shadow-2xl relative z-10 animate-in fade-in zoom-in duration-500">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 mb-4 shadow-lg">
            <Zap className="text-white" size={24} fill="currentColor" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            {isLoginMode ? t('auth.welcome') : t('auth.create')}
          </h2>
          <p className="text-slate-400 text-sm">
            {isLoginMode ? t('auth.loginDesc') : t('auth.registerDesc')}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center font-medium">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase ml-1">{t('auth.email')}</label>
            <div className="relative">
              <Mail className="absolute left-4 top-3.5 text-slate-500" size={18} />
              <input 
                type="email" 
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl py-3 pl-11 pr-4 text-white focus:border-blue-500 outline-none transition-all placeholder:text-slate-600"
                placeholder={t('auth.placeholderEmail')}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase ml-1">{t('auth.password')}</label>
            <div className="relative">
              <Lock className="absolute left-4 top-3.5 text-slate-500" size={18} />
              <input 
                type="password" 
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl py-3 pl-11 pr-4 text-white focus:border-blue-500 outline-none transition-all placeholder:text-slate-600"
                placeholder={t('auth.placeholderPass')}
              />
            </div>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {loading ? t('auth.processing') : (
              <>
                {isLoginMode ? t('auth.loginBtn') : t('auth.registerBtn')} <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-slate-400 text-sm">
            {isLoginMode ? t('auth.noAccount') : t('auth.hasAccount')}
            <button 
              onClick={() => {
                  setIsLoginMode(!isLoginMode);
                  setError('');
              }}
              className="text-blue-400 font-bold ml-2 hover:text-blue-300 transition-colors"
            >
              {isLoginMode ? t('auth.switchRegister') : t('auth.switchLogin')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};